/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo, useState, useEffect, useRef } from 'react'

import { useVideoPlayerMediaConfigQuery } from '@generated/graphql'
import CloudinaryVideo from './components/CloudinaryVideo/CloudinaryVideo'
import * as S from './VideoPlayer.styles'
import { createApplicationPage } from '@test/selectors'
import { Skeleton } from '@material-ui/lab'

export enum VideoType {
  APPLICATION_VIDEO = 'APPLICATION_VIDEO',
  REVIEW_VIDEO = 'REVIEW_VIDEO',
}

export type Props = {
  cloudinaryPublicId: string
  height?: number | string
  width?: number | string
  type: keyof typeof VideoType
  onMouseEnter?: (e: MouseEvent) => void
  onMouseLeave?: (e: MouseEvent) => void
  hidePlayButton?: boolean
  showControls?: boolean
  showSkeleton?: boolean
  transformation?: { [key: string]: string | number }
  onTrackingPlayEvent?: () => void
  poster?: string
  cohortId?: string
  stopVideo?: boolean
}

const VideoPlayer: React.FC<Props> = ({
  cloudinaryPublicId,
  height = 450,
  width = 250,
  type,
  onMouseEnter,
  onMouseLeave,
  hidePlayButton = false,
  showControls = false,
  showSkeleton = false,
  transformation = null,
  onTrackingPlayEvent,
  poster,
  cohortId = '',
  stopVideo = false,
}) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const videoRef = useRef<any>(null)
  const { data, loading } = useVideoPlayerMediaConfigQuery({
    fetchPolicy: 'cache-first',
    variables: { cohortId: cohortId },
  })

  const playVideo = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    if (videoRef.current.element) {
      videoRef.current.element.current.play()
    } else if (videoRef.current.play) {
      videoRef.current.play()
    }
    setIsPlaying(true)

    if (onTrackingPlayEvent) {
      onTrackingPlayEvent()
    }
  }

  const pauseVideo = () => {
    if (!isPlaying) {
      return
    }
    if (videoRef.current.element) {
      videoRef.current.element.current.pause()
    } else if (videoRef.current.play) {
      videoRef.current.pause()
    }
    setIsPlaying(false)
  }

  useEffect(() => {
    if (stopVideo) {
      pauseVideo()
    }
  }, [stopVideo])

  if (loading) {
    return showSkeleton ? (
      <Skeleton animation="wave" variant="rect" height={height} width={width} />
    ) : null
  }
  if (!transformation) {
    transformation =
      transformation || !!cohortId
        ? data?.MediaConfig.APPLICATION_SHAREABLE_ASSET_VIDEO?.transformation
        : type === VideoType.APPLICATION_VIDEO
        ? data?.MediaConfig.APPLICATION_VIDEO_720p.transformation
        : data?.MediaConfig.REVIEW_VIDEO_720p_WITH_WATERMARK_05022022.transformation
  }

  return (
    <S.VideoContainer
      onClick={pauseVideo}
      isplaying={isPlaying ? 1 : 0}
      data-test-id={createApplicationPage.videoPlayer.testId}
    >
      {!!transformation && (
        <CloudinaryVideo
          ref={videoRef}
          cloudinaryPublicId={cloudinaryPublicId}
          height={height}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          width={width}
          onEnded={setIsPlaying}
          showControls={showControls}
          transformation={transformation}
          poster={poster}
        />
      )}
      {hidePlayButton !== true && (
        <S.PlayButton>
          {!isPlaying && (
            <S.IconButton aria-label={'play'} onClick={playVideo}>
              <img width={40} src="/images/play-icon.svg" alt="play" />
            </S.IconButton>
          )}
        </S.PlayButton>
      )}
    </S.VideoContainer>
  )
}

export default memo(VideoPlayer)
